import { FEATURE_FLAGS } from '@mondaygpt/constants'
import { useCallback, useState } from 'react'
import { useFeatureGate } from 'modules/feature-flags'
import {
  LINKEDIN_PERSONAL_PROVIDER,
  linkedinModeToProvider,
  type LinkedInMode,
} from 'modules/oauth/utils/linkedin-connect'
import { LinkedInConnectChooser } from '../components/LinkedInConnectChooser'

/**
 * Shared LinkedIn Personal-vs-Company-Page connect chooser.
 *
 * The single canonical `linkedin` integration row fronts two OAuth apps
 * (`linkedin` personal, `linkedin_org` Company Page). This hook centralizes the
 * "which provider should the popup open?" decision so every connect surface
 * offers the same chooser UX. Everything downstream (verify/refetch/status) stays
 * keyed on the card provider `linkedin` and is unaffected — callers only swap the
 * provider string passed to openOAuthPopup.
 */
export function useLinkedInConnect() {
  const enabled = useFeatureGate(FEATURE_FLAGS.enableLinkedinOrg)
  const [pending, setPending] = useState<{ resolve: (p: string | null) => void } | null>(null)

  // Given the provider a surface wants to connect, return the provider to actually
  // open the popup with. Non-LinkedIn or gate-off → passthrough (returns input).
  // LinkedIn + gate-on → open the chooser and resolve to 'linkedin' | 'linkedin_org';
  // resolves to null if the user dismissed the chooser (caller should abort/skip).
  const resolveConnectProvider = useCallback(
    async (provider: string): Promise<string | null> => {
      if (provider !== LINKEDIN_PERSONAL_PROVIDER || !enabled) return provider
      return new Promise<string | null>(resolve => setPending({ resolve }))
    },
    [enabled]
  )

  const chooser = (
    <LinkedInConnectChooser
      open={!!pending}
      onOpenChange={open => {
        if (!open && pending) {
          pending.resolve(null)
          setPending(null)
        }
      }}
      onChoose={(mode: LinkedInMode) => {
        pending?.resolve(linkedinModeToProvider(mode))
        setPending(null)
      }}
    />
  )

  return { resolveConnectProvider, chooser }
}
